import React, { useState } from "react";
import { ComponentConfig, usePuck } from "@measured/puck";
import Wrapper from "../../../utils/Wrapper";
import { EditableButton, EditableText } from "../../../utils/components";
import { useWebsiteContext } from "../../../../context/website";
import { type globalCommonFieldsType, createDefaultProps, globalCommonFields, type textFieldsType, textFields } from "../../../utils/CommonFields";
import { generateSpacingCSS, generateBorderCSS, GetCurrentFont } from '../../../helpers'
import { themes } from "../../../../theme";

export type NewsLetterProps = globalCommonFieldsType & {
    title: {
        text: string;
        typography: textFieldsType
    };
    description: {
        text: string;
        typography: textFieldsType
    };
    align?: "left" | "center";
    textAlign?: "left" | "center";
    email: {
        text: string;
        typography: textFieldsType
    };
    button: (globalCommonFieldsType & {
        text: string;
        typography: textFieldsType
    });
};

export const NewsLetter: ComponentConfig<NewsLetterProps> = {
    fields: {
        title: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Title"
        },
        description: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Description"
        },
        align: {
            type: "radio",
            options: [
                { label: "left", value: "left" },
                { label: "center", value: "center" },
            ],
            label: "Letter Align"
        },
        textAlign: {
            type: "radio",
            options: [
                { label: "left", value: "left" },
                { label: "center", value: "center" },
            ],
            label: "Text Align"
        },
        email: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Email"
        },
        button: {
            type: "object",
            objectFields: {
                text: { type: "text", label: "Number" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
                ...globalCommonFields
            },
            label: "Button"
        },
        ...globalCommonFields
    },
    defaultProps: {
        title: {
            text: "Subscribe to our Newsletter",
            typography: createDefaultProps("text", {
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1.05,
            }),
        },
        align: "left",
        textAlign: "left",
        description: {
            text: "Join our salon and spa newsletter for insider access to exclusive offers, expert beauty tips, and the latest in wellness trends. Discover your path to ultimate relaxation and radiance.",
            typography: createDefaultProps("text", {
                fontSize: 20,
                fontWeight: 500,
                lineHeight: 1.75,
            }),
        },
        email: {
            text: "example@gmail.com",
            typography: createDefaultProps("text", {
                fontSize: 35,
                fontWeight: 600,
                lineHeight: 1.3,
            }),
        },
        button: {
            text: "Subscribe",
            typography: createDefaultProps("text"),
            ...createDefaultProps("globalCommon", {
                spacing: { padding: { left: 14, top: 14, right: 14, bottom: 14, } },
                backgroundColor: { hex: themes.theme_1.colors.secondary }
            })
        },
        ...createDefaultProps("globalCommon", { spacing: { padding: { left: 0, top: 64, right: 0, bottom: 64, } } })
    },
    render: (data) => {
        const { websiteTheme } = useWebsiteContext();

        const handleChange = (e, type) => {
            e.preventDefault();
            const updatedData = { ...data };
            if (type === 'title') {
                updatedData.title.text = e.target.textContent;
            } else if (type === 'description') {
                updatedData.description.text = e.target.textContent;
            } else if (type === 'button') {
                updatedData.button.text = e.target.textContent;
            }

            return { updatedData: updatedData, type: "NewsLetter_theme_6_variant_1" }
        };

        const theme = websiteTheme.name === "theme_6" ? websiteTheme.content : themes.theme_6
        const backgroundColor = data.backgroundColor.hex === "Default" ? theme.colors.primary : data.backgroundColor.hex

        return (
            <Wrapper styles={{ backgroundColor: backgroundColor, ...generateBorderCSS(data.border), color: theme.colors.text }}
            >
                <div style={{ padding: generateSpacingCSS(data.spacing.padding), margin: generateSpacingCSS(data.spacing.margin) }} className={`${data.textAlign === "left" ? "tw-text-left" : "tw-text-center"} tw-relative tw-isolate tw-overflow-hidden tw-py-16 sm:py-24 lg:py-32`}>
                    <div className={`${data.align === "left" ? "" : "tw-mx-auto"} tw-max-w-3xl lg:tw-max-w-4xl`}>
                        <EditableText handleChange={(e) => handleChange(e, "title")} data={data.title.typography}>
                            {data.title.text}
                        </EditableText>
                        <EditableText classes="tw-mt-4" handleChange={(e) => handleChange(e, "description")} data={data.description.typography}>
                            {data.description.text}
                        </EditableText>
                        <div className={`${data.align === "left" ? "" : "tw-mx-auto"} tw-mt-6 tw-flex tw-max-w-md tw-gap-x-4`}>
                            <label htmlFor="email-address" className="tw-sr-only">
                                Email address
                            </label>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="tw-min-w-0 tw-flex-auto tw-rounded-md tw-border-0 tw-bg-white/5 tw-px-3.5 tw-py-2 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-black/10"
                                placeholder="Enter your email"
                            />
                            <EditableButton handleChange={(e) => handleChange(e, "button")} styles={{ padding: generateSpacingCSS(data.button.spacing.padding), margin: generateSpacingCSS(data.button.spacing.margin), backgroundColor: data.button.backgroundColor.hex, ...generateBorderCSS(data.button.border) }} data={{ typography: data.button.typography }} isSubmit>
                                {data.button.text}
                            </EditableButton>
                        </div>
                    </div>
                    <div className="tw-absolute tw-left-1/2 tw-top-0 tw--z-10 tw--translate-x-1/2 tw-blur-3xl tw-xl:-top-6" aria-hidden="true">
                        <div
                            className="tw-aspect-[1155/678] tw-w-[72.1875rem] tw-bg-gradient-to-tr tw-from-[#b58074] tw-to-[#8284de] tw-opacity-30"
                            style={{
                                clipPath:
                                    'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
                            }}
                        />
                    </div>
                </div>
            </Wrapper>
        );
    },
};